import dayjs from 'dayjs';
import { UnitActivity, UnitActivityDisplay } from 'types';

export const transformUnitActivity = (
  currentRevision: UnitActivity,
  location: string | undefined,
  previousRevision?: UnitActivity,
  nextRevision?: UnitActivity
): UnitActivityDisplay => {
  return {
    timestamp: currentRevision.revision.timestamp,
    action: buildActivityAction(currentRevision),
    modifiedBy: currentRevision.unit.lastUpdatedBy ?? '',
    description: buildActivityDescription(currentRevision, previousRevision, nextRevision),
    unitNumber: currentRevision.unit.unitNumber ?? '',
    gateInTransaction: currentRevision.unit.gateInTransaction?.toString() ?? 'N/A',
    gateOutTransaction: currentRevision.unit.gateOutTransaction?.toString() ?? 'N/A',
    booking: currentRevision.unit.booking,
    currId: currentRevision.unit.id,
    prevId: previousRevision?.unit?.id,
    nextId: nextRevision?.unit?.id
  };
};

const buildActivityAction = (activity: UnitActivity): string => {
  if (!activity.type) {
    return 'TRANSFER';
  } else if (activity.type === 'ADD') {
    return 'GATE IN';
  } else if (activity.type === 'MOD') {
    if (activity.updatedFields.some(field => ["statusEdiFileExecutionId"].includes(field))) {
      return "EDI sent"
    } else {
      return activity.updatedFields.includes('active') && !activity.unit.active ? 'GATE OUT' : 'EDIT UNIT CHANGE';
    }
  }

  // TODO: add support for EDI

  // unreachable since we don't support deleting units
  return 'EDIT UNIT CHANGE';
};

const buildActivityDescription = (
  currentRevision: UnitActivity,
  previousRevision?: UnitActivity,
  nextRevision?: UnitActivity
): JSX.Element | string => {
  console.log("parsing")
  if (currentRevision.type === null) {
    return nextRevision && nextRevision.unit.id !== currentRevision.unit.id
      ? `TRANSFERRED FROM ${currentRevision?.unit.unitNumber} (${currentRevision?.unit.customerName ?? 'N/A'})`
      : `TRANSFERRED TO ${currentRevision?.unit.unitNumber} (${currentRevision?.unit.customerName ?? 'N/A'})`;
  } else if (currentRevision.type === 'ADD') {
    return '';
  } else if (currentRevision.updatedFields.length === 0 || !previousRevision) {
    return 'NO CHANGES';
  } else if (currentRevision.updatedFields.some(
    field => ["statusEdiFileExecutionId"].includes(field)
  )) {
    console.log(currentRevision.updatedFields);
    const label = "Status Edi";
    const executionId = currentRevision.unit.statusEdiFileExecutionId;
    return (
      <a
        href= {`https://www.stedi.com/app/core/file-executions/${executionId}`}
        target = "_blank"
        rel = "noopener noreferrer"
      >
        {label}
      </a>
    );
  }


  let description = 'CHANGED FIELD(S) ';
  let bookingDescription = '';
  const changelog: string[] = [];

  for (const field of currentRevision.updatedFields) {
    switch (field) {
      /* common fields */
      case 'booking':
        bookingDescription = `ASSOCIATED WITH BOOKING ${String(currentRevision.unit.booking)}`;
        break;
      case 'unitNumber':
        changelog.push(
          `UNIT NUMBER FROM '${String(previousRevision.unit.unitNumber)}' TO '${String(
            currentRevision.unit.unitNumber
          )}'`
        );
        break;
      case 'sizeType':
        changelog.push(
          `SIZE TYPE FROM '${String(previousRevision.unit.sizeType)}' TO '${String(currentRevision.unit.sizeType)}'`
        );
        break;
      case 'statusReason':
        changelog.push(
          `STATUS NOTES FROM '${String(previousRevision.unit.statusReason)}' TO '${String(
            currentRevision.unit.statusReason
          )}'`
        );
        break;
      case 'unitGrade':
        changelog.push(
          `UNIT GRADE FROM '${String(previousRevision.unit.unitGrade)}' TO '${String(currentRevision.unit.unitGrade)}'`
        );
        break;
      case 'unitStatus':
        changelog.push(
          `UNIT STATUS FROM '${String(previousRevision.unit.unitStatus)}' TO '${String(
            currentRevision.unit.unitStatus
          )}'`
        );
        break;
      case 'active':
        changelog.push(
          `ACTIVE FROM '${String(previousRevision.unit.active)}' TO '${String(currentRevision.unit.active)}'`
        );
        break;
      case 'onHold':
        changelog.push(
          `ON HOLD FROM '${String(previousRevision.unit.onHold)}' TO '${String(currentRevision.unit.onHold)}'`
        );
        break;
      case 'holdNotes':
        changelog.push(
          `HOLD NOTES FROM '${String(previousRevision.unit.holdNotes)}' TO '${String(currentRevision.unit.holdNotes)}'`
        );
        break;
      case 'notes':
        changelog.push(
          `NOTES FROM '${String(previousRevision.unit.notes)}' TO '${String(currentRevision.unit.notes)}'`
        );
        break;

      /* container fields */
      case 'billOfLading':
        changelog.push(
          `BILL OF LADING FROM '${String(previousRevision.unit.billOfLading)}' TO '${String(
            currentRevision.unit.billOfLading
          )}'`
        );
        break;
      case 'hazard':
        changelog.push(
          `HAZARD FROM '${String(previousRevision.unit.hazard)}' TO '${String(currentRevision.unit.hazard)}'`
        );
        break;
      case 'loaded':
        changelog.push(
          `LOADED FROM '${String(previousRevision.unit.loaded)}' TO '${String(currentRevision.unit.loaded)}'`
        );
        break;
      case 'seal0':
        changelog.push(
          `SEAL #1 FROM '${String(previousRevision.unit.seal0)}' TO '${String(currentRevision.unit.seal0)}'`
        );
        break;
      case 'seal1':
        changelog.push(
          `SEAL #2 FROM '${String(previousRevision.unit.seal1)}' TO '${String(currentRevision.unit.seal1)}'`
        );
        break;
      case 'seal2':
        changelog.push(
          `SEAL #3 FROM '${String(previousRevision.unit.seal2)}' TO '${String(currentRevision.unit.seal2)}'`
        );
        break;
      case 'weight':
        changelog.push(
          `WEIGHT FROM '${String(previousRevision.unit.weight)}' TO '${String(currentRevision.unit.weight)}'`
        );
        break;
      case 'humidity':
        changelog.push(
          `HUMIDITY FROM '${String(previousRevision.unit.humidity)}' TO '${String(currentRevision.unit.humidity)}'`
        );
        break;
      case 'temperature':
        changelog.push(
          `TEMPERATURE FROM '${String(previousRevision.unit.temperature)}' TO '${String(
            currentRevision.unit.temperature
          )}'`
        );
        break;
      case 'ventilation':
        changelog.push(
          `VENTILATION FROM '${String(previousRevision.unit.ventilation)}' TO '${String(
            currentRevision.unit.ventilation
          )}'`
        );
        break;

      /* chassis fields */
      case 'flatbed':
        changelog.push(
          `FLATBED FROM '${String(previousRevision.unit.flatbed)}' TO '${String(currentRevision.unit.flatbed)}'`
        );
        break;
      case 'fmcsaDate':
        changelog.push(
          `FMCSA DATE FROM '${String(previousRevision.unit.fmcsaDate)}' TO '${String(currentRevision.unit.fmcsaDate)}'`
        );
        break;
      case 'ownerChassis':
        changelog.push(
          `OWNER CHASSIS FROM '${String(previousRevision.unit.ownerChassis)}' TO '${String(
            currentRevision.unit.ownerChassis
          )}'`
        );
        break;
      case 'licensePlate':
        changelog.push(
          `LICENSE PLATE FROM '${String(previousRevision.unit.licensePlate)}' TO '${String(
            currentRevision.unit.licensePlate
          )}'`
        );
        break;
      case 'licensePlateExpirationDate':
        changelog.push(
          `LICENSE PLATE EXPIRATION FROM '${dayjs(previousRevision.unit.licensePlateExpirationDate).format(
            'MM/DD/YYYY'
          )}' TO '${dayjs(currentRevision.unit.licensePlateExpirationDate).format('MM/DD/YYYY')}'`
        );
        break;

      /* genset fields */
      case 'fuelLevel':
        changelog.push(
          `FUEL LEVEL FROM '${String(previousRevision.unit.fuelLevel)}' TO '${String(currentRevision.unit.fuelLevel)}'`
        );
        break;

      default:
        break;
  }
}

return `${changelog.length > 0 ? `${description} ${changelog.join(',')}.` : ''}${bookingDescription.length > 0 ? ` ${bookingDescription}.` : ''
  }`.toUpperCase();
};
